import { useDocumentTitle } from '@mantine/hooks'
import { TooltipProvider } from '@neo/primitives/base/tootlip'
import { ReactComponent as PlivoLogo } from '@shared/assets/illustrations/login/plivo.svg'
import type { PropsWithChildren, ReactNode } from 'react'
import { VoiceAiPanel } from './voice-ai-panel'

interface AuthLayoutProps extends PropsWithChildren {
  title: ReactNode
  description: ReactNode
  pageTitle: string
  footer?: ReactNode
}

/**
 * Every auth page: the voice-AI panel beside the form on desktop, nothing but
 * the form below `lg`. The panel carries the brand mark on desktop, so mobile
 * gets its own logo above the form.
 */
export const AuthLayout = ({
  pageTitle,
  title,
  description,
  children,
  footer,
}: AuthLayoutProps) => {
  useDocumentTitle(`${pageTitle || 'CX'} | Plivo`)

  return (
    <div className="relative flex h-screen min-h-screen w-full flex-col overflow-x-hidden overflow-y-auto lg:grid lg:grid-cols-[auto_1fr] lg:overflow-hidden xl:min-h-[800px]">
      <TooltipProvider>
        <div className="relative z-10 hidden overflow-hidden lg:block lg:h-[100vh] lg:w-[50vw]">
          <VoiceAiPanel />
        </div>
        <div className="relative z-10 flex min-w-0 flex-1 items-center justify-center bg-background px-6 py-10 lg:h-[100vh] lg:p-0">
          <div className="mx-auto grid min-w-0 w-full max-w-[420px] gap-6 font-inter">
            <a href="https://www.plivo.com/" aria-label="Plivo" className="lg:hidden">
              <PlivoLogo className="h-6 w-auto" />
            </a>
            {title && (
              <div className="grid gap-2">
                <h1 className="font-sora text-h1-500 text-foreground">{title}</h1>
                {description && (
                  <p className="max-w-[58ch] text-p-400 leading-[1.55] text-secondary">
                    {description}
                  </p>
                )}
              </div>
            )}
            {children}
            {footer}
          </div>
        </div>
      </TooltipProvider>
    </div>
  )
}
